import { sql, relations } from 'drizzle-orm';
import {
  index,
  jsonb,
  pgTable,
  timestamp,
  varchar,
  integer,
  text,
  boolean,
  decimal,
  unique,
} from 'drizzle-orm/pg-core';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod';
import { isValidE164 } from './phoneUtils';

export function formatZodErrorResponse(error: z.ZodError, fallbackMessage = 'Invalid data') {
  const errors = error.errors.map((issue) => ({
    field: issue.path.join('.'),
    message: issue.message,
  }));
  const message =
    errors.map((issue) => `${issue.field}: ${issue.message}`).join('. ') || fallbackMessage;

  return { message, errors };
}

// Session storage table for authentication
export const sessions = pgTable(
  'sessions',
  {
    sid: varchar('sid').primaryKey(),
    sess: jsonb('sess').notNull(),
    expire: timestamp('expire').notNull(),
  },
  (table) => [index('IDX_session_expire').on(table.expire)]
);

// User roles enum
export const userRoleEnum = ['user', 'hr_admin', 'content_admin', 'super_admin'] as const;
export type UserRole = (typeof userRoleEnum)[number];

// Users table
export const users = pgTable('users', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: varchar('email').unique().notNull(),
  passwordHash: varchar('password_hash').notNull(),
  firstName: varchar('first_name'),
  lastName: varchar('last_name'),
  profileImageUrl: varchar('profile_image_url'),
  companyId: varchar('company_id'),
  department: varchar('department'),
  phoneNumber: varchar('phone_number').unique(),
  isPhoneVerified: boolean('is_phone_verified').default(false),
  isEmailVerified: boolean('is_email_verified').default(false),
  role: varchar('role').default('user'), // user, hr_admin, content_admin, super_admin
  lastLoginAt: timestamp('last_login_at'),
  refreshToken: varchar('refresh_token'),
  refreshTokenExpiresAt: timestamp('refresh_token_expires_at'),
  /** Set when the 30-day SWYGFEST report availability email was sent */
  reportAvailabilityEmailSentAt: timestamp('report_availability_email_sent_at'),
  /** Terms & Conditions acceptance */
  termsAcceptedAt: timestamp('terms_accepted_at'),
  termsVersion: varchar('terms_version'),
  /** Privacy Policy acceptance */
  privacyAcceptedAt: timestamp('privacy_accepted_at'),
  privacyVersion: varchar('privacy_version'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

/** Current legal document versions recorded on acceptance */
export const CURRENT_TERMS_VERSION = '2026-08-01';
export const CURRENT_PRIVACY_VERSION = '2026-08-01';

// Companies table
export const companies = pgTable('companies', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: varchar('name').notNull(),
  domain: varchar('domain').unique().notNull(),
  code: varchar('code').unique().notNull(),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
});

// Whitelisted email domains for registration restrictions
export const whitelistedDomains = pgTable('whitelisted_domains', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  domain: varchar('domain').unique().notNull(), // e.g., "company.com"
  companyName: varchar('company_name'), // Optional company name
  isActive: boolean('is_active').default(true),
  autoAssignRole: varchar('auto_assign_role').default('user'), // Role for users from this domain
  allowSubdomains: boolean('allow_subdomains').default(false), // Allow *.company.com
  createdBy: varchar('created_by').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// User profiles with hydration settings
export const userProfiles = pgTable('user_profiles', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar('user_id')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  avatarName: varchar('avatar_name'),
  avatarType: varchar('avatar_type'), // For visual avatar selection
  profilePictureUrl: text('profile_picture_url'), // UploadThing URL for profile picture
  age: integer('age'),
  gender: varchar('gender'),
  reminderInterval: integer('reminder_interval').default(45), // minutes
  activeHoursStart: varchar('active_hours_start').default('09:00'),
  activeHoursEnd: varchar('active_hours_end').default('18:00'),
  timezone: varchar('timezone').default('Asia/Kolkata'), // IANA timezone (e.g., "Asia/Kolkata" for IST)
  activeDays: text('active_days')
    .array()
    .default(sql`ARRAY['monday','tuesday','wednesday','thursday','friday']`),
  dailyTarget: integer('daily_target').default(2500), // ml
  avgTimesPerDay: integer('avg_times_per_day'),
  avgVolumePerDay: integer('avg_volume_per_day'),
  currentDailyWaterIntake: integer('current_daily_water_intake').default(1500), // ml - baseline
  currentDrinkingFrequency: integer('current_drinking_frequency').default(60), // minutes
  proratedDailyTarget: integer('prorated_daily_target'), // calculated based on active hours
  volumePerReminder: integer('volume_per_reminder'), // calculated volume per reminder
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Water intake logs
export const waterIntakeLogs = pgTable('water_intake_logs', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar('user_id')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  volume: integer('volume').notNull(), // ml
  loggedAt: timestamp('logged_at').defaultNow(),
  source: varchar('source').default('manual'), // manual, reminder, onesignal, etc.
});

// OneSignal player IDs (device subscriptions)
export const oneSignalPlayers = pgTable('onesignal_players', {
  id: varchar('id')
    .primaryKey()
    .$defaultFn(() => crypto.randomUUID()),
  userId: varchar('user_id')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  playerId: varchar('player_id').notNull().unique(), // OneSignal player ID
  deviceType: varchar('device_type').default('web'), // web, ios, android
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// OneSignal notification tracking (per user, per day)
export const oneSignalNotificationTracking = pgTable(
  'onesignal_notification_tracking',
  {
    id: varchar('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    date: varchar('date').notNull(), // YYYY-MM-DD

    // Core tracking metrics (Requirements.md E.1)
    successfulIntakes: integer('successful_intakes').default(0), // Water logged BEFORE deadline
    reminderCount: integer('reminder_count').default(0), // Total reminders sent today
    consecutiveMisses: integer('consecutive_misses').default(0), // Consecutive missed deadlines
    missCount: integer('miss_count').default(0), // Total missed reminder intervals today (never decreases)

    // Color tier system (Requirements.md E.1)
    currentColorTier: varchar('current_color_tier').default('purple'), // purple, blue, green, yellow, orange, red

    // Deadline tracking
    nextDeadline: timestamp('next_deadline'), // Next notification deadline
    lastNotificationSent: timestamp('last_notification_sent'), // Last notification timestamp

    // Daily volume tracking
    currentDayVolume: integer('current_day_volume').default(0), // Total ml logged today
    dailyGoal: integer('daily_goal').default(2500), // Prorated daily goal

    // Notification state
    isActive: boolean('is_active').default(true), // Whether notifications are enabled

    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => [unique().on(table.userId, table.date)]
);

// Hydration notifications - REMOVED (replaced with OneSignal)

// User badges and achievements
export const userBadges = pgTable('user_badges', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar('user_id')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  badgeType: varchar('badge_type').notNull(), // streak, volume, frequency, etc.
  badgeName: varchar('badge_name').notNull(),
  description: text('description'),
  earnedAt: timestamp('earned_at').defaultNow(),
});

// Daily hydration stats
export const dailyStats = pgTable(
  'daily_stats',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    date: varchar('date').notNull(), // YYYY-MM-DD
    totalVolume: integer('total_volume').default(0),
    targetVolume: integer('target_volume').default(2500),
    frequencyScore: decimal('frequency_score', { precision: 5, scale: 2 }).default('0'),
    volumeScore: decimal('volume_score', { precision: 5, scale: 2 }).default('0'),
    overallScore: decimal('overall_score', { precision: 5, scale: 2 }).default('0'),
    streakDays: integer('streak_days').default(0),
    successfulReminders: integer('successful_reminders').default(0),
    totalReminders: integer('total_reminders').default(0),
    consecutiveMisses: integer('consecutive_misses').default(0),
    missCount: integer('miss_count').default(0),
    volumeBadgeName: varchar('volume_badge_name'),
    frequencyBadgeName: varchar('frequency_badge_name'),
    volumeBadgePercent: decimal('volume_badge_percent', { precision: 7, scale: 2 }),
    badgesFinalized: boolean('badges_finalized').default(false),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => [unique().on(table.userId, table.date)]
);

// Notification tracking - REMOVED (replaced with OneSignal)

// Push subscriptions - REMOVED (replaced with OneSignal)

// Email verification tokens (legacy - kept for backward compatibility)
export const emailVerificationTokens = pgTable('email_verification_tokens', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: varchar('email').notNull(),
  token: varchar('token').notNull().unique(),
  expiresAt: timestamp('expires_at').notNull(),
  createdAt: timestamp('created_at').defaultNow(),
});

// Email OTP tokens (new OTP-based verification)
export const emailOtpTokens = pgTable('email_otp_tokens', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: varchar('email').notNull(),
  otp: varchar('otp').notNull(),
  expiresAt: timestamp('expires_at').notNull(),
  attempts: integer('attempts').default(0),
  isUsed: boolean('is_used').default(false),
  createdAt: timestamp('created_at').defaultNow(),
});

// Phone verification tokens
export const phoneVerificationTokens = pgTable('phone_verification_tokens', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  phoneNumber: varchar('phone_number').notNull(),
  code: varchar('code').notNull(),
  expiresAt: timestamp('expires_at').notNull(),
  attempts: integer('attempts').default(0),
  isUsed: boolean('is_used').default(false),
  createdAt: timestamp('created_at').defaultNow(),
});

// Password reset tokens
export const passwordResetTokens = pgTable('password_reset_tokens', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: varchar('email').notNull(),
  token: varchar('token').notNull().unique(),
  expiresAt: timestamp('expires_at').notNull(),
  used: boolean('used').default(false),
  createdAt: timestamp('created_at').defaultNow(),
});

// FCM tokens table - deprecated (using native browser notifications now)
// Keeping table definition for backward compatibility but not using in code
export const fcmTokens = pgTable('fcm_tokens', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar('user_id')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  token: text('token').notNull().unique(),
  deviceType: varchar('device_type').default('web'),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Referral requests for company partnerships
export const referralRequests = pgTable('referral_requests', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userEmail: varchar('user_email').notNull(),
  userName: varchar('user_name').notNull(),
  companyCode: varchar('company_code'),
  status: varchar('status').notNull().default('template_sent'), // template_sent, contacted, completed
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Whitelisted emails for signup restrictions
export const whitelistedEmails = pgTable('whitelisted_emails', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: varchar('email').unique().notNull(),
  companyId: varchar('company_id').references(() => companies.id, { onDelete: 'cascade' }),
  department: varchar('department'),
  assignedRole: varchar('assigned_role').default('user'), // user, hr_admin, content_admin
  status: varchar('status').default('pending'), // pending, registered, active
  invitedBy: varchar('invited_by').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// ============================================================================
// ADMIN CONTENT MANAGEMENT TABLES
// ============================================================================

// Learning content for hydration tips and articles
export const learningContent = pgTable('learning_content', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  headline: varchar('headline').notNull(),
  subHeading: varchar('sub_heading'), // New: subtitle/sub-heading
  bodyText: text('body_text'), // New: full article content
  imageUrl: varchar('image_url'),
  headerImage: varchar('header_image'), // New: hero/header image for article
  thumbnailUrl: varchar('thumbnail_url'), // New: thumbnail for list views
  linkUrl: varchar('link_url').notNull(),
  section: varchar('section').default('learning'), // New: 'know_the_app', 'learning', 'blogs', 'explainer'
  isFeatured: boolean('is_featured').default(false), // New: featured content flag
  /** 1 is the member's join day, 2 the next calendar day. Same sequence for everyone. */
  featureDay: integer('feature_day'),
  isPublished: boolean('is_published').default(false),
  companyId: varchar('company_id').references(() => companies.id, { onDelete: 'cascade' }), // null = global
  createdBy: varchar('created_by').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Motivational messages / homepage popups (text optional when image carries the message)
export const motivationalMessages = pgTable('motivational_messages', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  text: text('text').notNull().default(''),
  /** Full popup image; when set, the image typically contains headlines so extra text is optional */
  imageUrl: varchar('image_url'),
  isActive: boolean('is_active').default(true),
  /** Where the full-strip advertisement opens. Empty means the image is not a link. */
  linkUrl: varchar('link_url'),
  companyId: varchar('company_id').references(() => companies.id, { onDelete: 'cascade' }), // null = global
  createdBy: varchar('created_by').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Customizable badge messages
export const badgeMessages = pgTable('badge_messages', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  badgeName: varchar('badge_name').notNull(), // First Drop, Week Warrior, etc.
  badgeEmoji: varchar('badge_emoji').default('🏆'),
  message: text('message').notNull(),
  companyId: varchar('company_id').references(() => companies.id, { onDelete: 'cascade' }), // null = global
  createdBy: varchar('created_by').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Whitelisted users for company enrollment
export const whitelistedUsers = pgTable(
  'whitelisted_users',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    email: varchar('email').notNull(),
    department: varchar('department'),
    companyId: varchar('company_id')
      .notNull()
      .references(() => companies.id, { onDelete: 'cascade' }),
    status: varchar('status').default('pending'), // pending, active, registered
    addedBy: varchar('added_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => [unique().on(table.email, table.companyId)]
);

// Notification preferences - REMOVED (replaced with OneSignal)

// Relations
export const usersRelations = relations(users, ({ one, many }) => ({
  company: one(companies, {
    fields: [users.companyId],
    references: [companies.id],
  }),
  profile: one(userProfiles, {
    fields: [users.id],
    references: [userProfiles.userId],
  }),
  waterIntakeLogs: many(waterIntakeLogs),
  badges: many(userBadges),
  dailyStats: many(dailyStats),
  oneSignalPlayers: many(oneSignalPlayers),
  oneSignalNotificationTracking: many(oneSignalNotificationTracking),
}));

export const companiesRelations = relations(companies, ({ many }) => ({
  users: many(users),
  learningContent: many(learningContent),
  motivationalMessages: many(motivationalMessages),
  badgeMessages: many(badgeMessages),
  whitelistedUsers: many(whitelistedUsers),
}));

export const userProfilesRelations = relations(userProfiles, ({ one }) => ({
  user: one(users, {
    fields: [userProfiles.userId],
    references: [users.id],
  }),
}));

export const waterIntakeLogsRelations = relations(waterIntakeLogs, ({ one }) => ({
  user: one(users, {
    fields: [waterIntakeLogs.userId],
    references: [users.id],
  }),
}));

export const oneSignalPlayersRelations = relations(oneSignalPlayers, ({ one }) => ({
  user: one(users, {
    fields: [oneSignalPlayers.userId],
    references: [users.id],
  }),
}));

export const oneSignalNotificationTrackingRelations = relations(
  oneSignalNotificationTracking,
  ({ one }) => ({
    user: one(users, {
      fields: [oneSignalNotificationTracking.userId],
      references: [users.id],
    }),
  })
);

// Notification relations - REMOVED (replaced with OneSignal)

export const userBadgesRelations = relations(userBadges, ({ one }) => ({
  user: one(users, {
    fields: [userBadges.userId],
    references: [users.id],
  }),
}));

export const dailyStatsRelations = relations(dailyStats, ({ one }) => ({
  user: one(users, {
    fields: [dailyStats.userId],
    references: [users.id],
  }),
}));

// FCM token relations removed - using native notifications
// export const fcmTokensRelations = relations(fcmTokens, ({ one }) => ({
//   user: one(users, {
//     fields: [fcmTokens.userId],
//     references: [users.id],
//   }),
// }));

// Notification preferences and tracking relations - REMOVED (replaced with OneSignal)

// Admin content relations
export const learningContentRelations = relations(learningContent, ({ one }) => ({
  company: one(companies, {
    fields: [learningContent.companyId],
    references: [companies.id],
  }),
  creator: one(users, {
    fields: [learningContent.createdBy],
    references: [users.id],
  }),
}));

export const motivationalMessagesRelations = relations(motivationalMessages, ({ one }) => ({
  company: one(companies, {
    fields: [motivationalMessages.companyId],
    references: [companies.id],
  }),
  creator: one(users, {
    fields: [motivationalMessages.createdBy],
    references: [users.id],
  }),
}));

export const badgeMessagesRelations = relations(badgeMessages, ({ one }) => ({
  company: one(companies, {
    fields: [badgeMessages.companyId],
    references: [companies.id],
  }),
  creator: one(users, {
    fields: [badgeMessages.createdBy],
    references: [users.id],
  }),
}));

export const whitelistedUsersRelations = relations(whitelistedUsers, ({ one }) => ({
  company: one(companies, {
    fields: [whitelistedUsers.companyId],
    references: [companies.id],
  }),
  addedByUser: one(users, {
    fields: [whitelistedUsers.addedBy],
    references: [users.id],
  }),
}));

export const whitelistedDomainsRelations = relations(whitelistedDomains, ({ one }) => ({
  creator: one(users, {
    fields: [whitelistedDomains.createdBy],
    references: [users.id],
  }),
}));

// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  passwordHash: true,
  createdAt: true,
  updatedAt: true,
});

// Auth schemas
export const signupSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phoneNumber: z.string().optional(), // For non-company email registrations
  companyCode: z.string().optional(), // For company code validation
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const insertCompanySchema = createInsertSchema(companies).omit({
  id: true,
  createdAt: true,
});

export const insertWhitelistedDomainSchema = createInsertSchema(whitelistedDomains).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

const phoneNumberSchema = z
  .string()
  .min(1, 'Phone number is required')
  .refine((val) => isValidE164(val), {
    message: 'Enter a valid phone number with country code',
  });

/** Treat empty strings / null as undefined so optional numeric fields don't coerce to 0. */
const emptyToUndefined = (value: unknown) =>
  value === '' || value === null || value === undefined ? undefined : value;

const optionalAge = z.preprocess(
  emptyToUndefined,
  z.coerce
    .number({ invalid_type_error: 'Age must be a number' })
    .min(13, 'Age must be at least 13')
    .max(120, 'Age must be 120 or less')
    .optional()
);

const optionalDailyTarget = z.preprocess(
  emptyToUndefined,
  z.coerce
    .number({ invalid_type_error: 'Daily target must be a number' })
    .min(500, 'Daily target must be at least 500ml')
    .max(10000, 'Daily target must be 10000ml or less')
    .optional()
);

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  avatarName: 'Avatar name',
  phoneNumber: 'Phone number',
  age: 'Age',
  gender: 'Gender',
  currentDailyWaterIntake: 'Typical daily water intake',
  currentDrinkingFrequency: 'Drinking frequency',
  activeDays: 'Active days',
  activeHoursStart: 'Active hours start',
  activeHoursEnd: 'Active hours end',
  reminderInterval: 'Reminder interval',
  dailyTarget: 'Daily target',
  proratedDailyTarget: 'Prorated daily target',
  volumePerReminder: 'Volume per reminder',
  profilePictureUrl: 'Profile picture',
  avatarType: 'Avatar type',
};

export type ProfileValidationIssue = { field: string; message: string };

/** Format Zod issues into field-level errors and a user-facing summary message. */
export function formatProfileValidationErrors(error: z.ZodError): {
  message: string;
  errors: ProfileValidationIssue[];
} {
  const errors = error.errors.map((e) => {
    const field = e.path.join('.') || 'form';
    const label = FIELD_LABELS[field] || field;
    let message = e.message;
    if (message.startsWith('Number must be greater than or equal to')) {
      message = `${label} ${message.replace('Number must be ', 'must be ').replace('greater than or equal to', 'at least')}`;
    } else if (message.startsWith('Number must be less than or equal to')) {
      message = `${label} ${message.replace('Number must be ', 'must be ').replace('less than or equal to', 'at most')}`;
    } else if (!message.toLowerCase().includes(label.toLowerCase()) && field !== 'form') {
      if (
        !message.toLowerCase().includes('required') &&
        !message.toLowerCase().startsWith(label.split(' ')[0].toLowerCase())
      ) {
        message = `${label}: ${message}`;
      }
    }
    return { field, message };
  });

  const uniqueMessages = [...new Set(errors.map((e) => e.message))];
  return {
    message: uniqueMessages.join('. ') || 'Invalid profile data',
    errors,
  };
}

// Enhanced profile creation schema with mandatory fields
export const profileCreationSchema = z.object({
  // Mandatory fields
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  avatarName: z.string().min(1, 'Avatar name is required'),
  phoneNumber: phoneNumberSchema,

  // Optional fields
  avatarType: z.string().optional(),
  profilePictureUrl: z.string().optional(),
  age: optionalAge,
  gender: z
    .enum(['male', 'female', 'other', 'prefer-not-to-say'], {
      errorMap: () => ({ message: 'Gender must be male, female, other, or prefer-not-to-say' }),
    })
    .optional(),
  currentDailyWaterIntake: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(500, 'Typical daily water intake must be at least 500ml')
      .max(5000, 'Typical daily water intake must be 5000ml or less')
      .default(1500)
  ),
  currentDrinkingFrequency: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(30, 'Drinking frequency must be at least 30 minutes')
      .max(240, 'Drinking frequency must be 240 minutes or less')
      .default(60)
  ),

  // Active tracking configuration
  activeDays: z
    .array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']), {
      errorMap: () => ({
        message: 'Active days must use full weekday names (e.g. monday)',
      }),
    })
    .min(1, 'Select at least one active day')
    .default(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']),
  activeHoursStart: z.string().min(1, 'Active hours start is required').default('09:00'),
  activeHoursEnd: z.string().min(1, 'Active hours end is required').default('18:00'),

  // Reminder settings
  reminderInterval: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .refine((val) => [45, 60, 90].includes(val), {
        message: 'Reminder interval must be 45, 60, or 90 minutes',
      })
      .default(45)
  ),

  // Notification preferences
  waterReminders: z.boolean().default(true),
  achievementNotifications: z.boolean().default(true),
  soundEnabled: z.boolean().default(true),

  // User-set daily volume goal (ml) from the onboarding "Your Goal" step.
  // Authoritative target; when provided it overrides the auto-calculated goal.
  dailyTarget: optionalDailyTarget,

  // Calculated fields (optional, will be computed if not provided)
  proratedDailyTarget: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(500, 'Prorated daily target must be at least 500ml')
      .max(10000, 'Prorated daily target must be 10000ml or less')
      .optional()
  ),
  volumePerReminder: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(50, 'Volume per reminder must be at least 50ml')
      .max(1000, 'Volume per reminder must be 1000ml or less')
      .optional()
  ),
});

// Profile completion validation schema
export const profileCompletionSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  avatarName: z.string().min(1, 'Avatar name is required'),
  phoneNumber: phoneNumberSchema,
  isPhoneVerified: z.boolean().refine((val) => val === true, {
    message: 'Phone number must be verified to complete profile setup',
  }),
});

// Enhanced profile update schema that handles both user and profile data
export const profileUpdateSchema = z.object({
  // User table fields
  firstName: z.string().min(1, 'First name is required').optional(),
  lastName: z.string().min(1, 'Last name is required').optional(),
  phoneNumber: phoneNumberSchema.optional(),

  // Profile table fields
  avatarName: z.string().optional(),
  avatarType: z.string().optional(),
  profilePictureUrl: z.string().optional(),
  age: optionalAge,
  gender: z
    .enum(['male', 'female', 'other', 'prefer-not-to-say'], {
      errorMap: () => ({ message: 'Gender must be male, female, other, or prefer-not-to-say' }),
    })
    .optional(),
  dailyTarget: optionalDailyTarget,
  reminderInterval: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(30, 'Reminder interval must be at least 30 minutes')
      .max(240, 'Reminder interval must be 240 minutes or less')
      .optional()
  ),
  activeHoursStart: z.string().optional(),
  activeHoursEnd: z.string().optional(),
  activeDays: z
    .array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']), {
      errorMap: () => ({
        message: 'Active days must use full weekday names (e.g. monday)',
      }),
    })
    .min(1, 'Select at least one active day')
    .optional(),
  currentDailyWaterIntake: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(500, 'Typical daily water intake must be at least 500ml')
      .max(5000, 'Typical daily water intake must be 5000ml or less')
      .optional()
  ),
  currentDrinkingFrequency: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(30, 'Drinking frequency must be at least 30 minutes')
      .max(240, 'Drinking frequency must be 240 minutes or less')
      .optional()
  ),
  proratedDailyTarget: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(500, 'Prorated daily target must be at least 500ml')
      .max(10000, 'Prorated daily target must be 10000ml or less')
      .optional()
  ),
  volumePerReminder: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(50, 'Volume per reminder must be at least 50ml')
      .max(1000, 'Volume per reminder must be 1000ml or less')
      .optional()
  ),
});

export const insertWaterIntakeLogSchema = createInsertSchema(waterIntakeLogs).omit({
  id: true,
  loggedAt: true,
});

export const insertOneSignalPlayerSchema = createInsertSchema(oneSignalPlayers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOneSignalNotificationTrackingSchema = createInsertSchema(
  oneSignalNotificationTracking
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Notification schema - REMOVED (replaced with OneSignal)

export const insertUserBadgeSchema = createInsertSchema(userBadges).omit({
  id: true,
  earnedAt: true,
});

export const insertDailyStatSchema = createInsertSchema(dailyStats).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailVerificationTokenSchema = createInsertSchema(emailVerificationTokens).omit({
  id: true,
  createdAt: true,
});

export const insertEmailOtpTokenSchema = createInsertSchema(emailOtpTokens).omit({
  id: true,
  createdAt: true,
});

export const insertPhoneVerificationTokenSchema = createInsertSchema(phoneVerificationTokens).omit({
  id: true,
  createdAt: true,
});

export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({
  id: true,
  createdAt: true,
});

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(6),
});

// FCM token schema removed - using native notifications
// export const insertFcmTokenSchema = createInsertSchema(fcmTokens).omit({
//   id: true,
//   createdAt: true,
//   updatedAt: true,
// });

// Notification preferences and tracking schemas - REMOVED (replaced with OneSignal)

// Admin content schemas
export const insertLearningContentSchema = createInsertSchema(learningContent).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertMotivationalMessageSchema = createInsertSchema(motivationalMessages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertBadgeMessageSchema = createInsertSchema(badgeMessages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertWhitelistedUserSchema = createInsertSchema(whitelistedUsers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;
export type Company = typeof companies.$inferSelect;
export type InsertCompany = z.infer<typeof insertCompanySchema>;
export type UserProfile = typeof userProfiles.$inferSelect;
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type WaterIntakeLog = typeof waterIntakeLogs.$inferSelect;
export type InsertWaterIntakeLog = z.infer<typeof insertWaterIntakeLogSchema>;
export type OneSignalPlayer = typeof oneSignalPlayers.$inferSelect;
export type InsertOneSignalPlayer = z.infer<typeof insertOneSignalPlayerSchema>;
export type OneSignalNotificationTracking = typeof oneSignalNotificationTracking.$inferSelect;
export type InsertOneSignalNotificationTracking = z.infer<
  typeof insertOneSignalNotificationTrackingSchema
>;
// Notification types - REMOVED (replaced with OneSignal)
export type UserBadge = typeof userBadges.$inferSelect;
export type InsertUserBadge = z.infer<typeof insertUserBadgeSchema>;
export type DailyStat = typeof dailyStats.$inferSelect;
export type InsertDailyStat = z.infer<typeof insertDailyStatSchema>;
export type EmailVerificationToken = typeof emailVerificationTokens.$inferSelect;
export type InsertEmailVerificationToken = z.infer<typeof insertEmailVerificationTokenSchema>;
export type EmailOtpToken = typeof emailOtpTokens.$inferSelect;
export type InsertEmailOtpToken = z.infer<typeof insertEmailOtpTokenSchema>;
export type PhoneVerificationToken = typeof phoneVerificationTokens.$inferSelect;
export type InsertPhoneVerificationToken = z.infer<typeof insertPhoneVerificationTokenSchema>;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;
export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;
// FCM token types removed - using native notifications
// export type FcmToken = typeof fcmTokens.$inferSelect;
// export type InsertFcmToken = z.infer<typeof insertFcmTokenSchema>;
// Notification preferences and tracking types - REMOVED (replaced with OneSignal)
export type ReferralRequest = typeof referralRequests.$inferSelect;
export type InsertReferralRequest = typeof referralRequests.$inferInsert;

// Admin content types
export type LearningContent = typeof learningContent.$inferSelect;
export type InsertLearningContent = z.infer<typeof insertLearningContentSchema>;
export type MotivationalMessage = typeof motivationalMessages.$inferSelect;
export type InsertMotivationalMessage = z.infer<typeof insertMotivationalMessageSchema>;
export type BadgeMessage = typeof badgeMessages.$inferSelect;
export type InsertBadgeMessage = z.infer<typeof insertBadgeMessageSchema>;
export type WhitelistedUser = typeof whitelistedUsers.$inferSelect;
export type InsertWhitelistedUser = z.infer<typeof insertWhitelistedUserSchema>;

// Auth types
export type SignupData = z.infer<typeof signupSchema>;
export type LoginData = z.infer<typeof loginSchema>;
export type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordData = z.infer<typeof resetPasswordSchema>;

// ============================================================================
// HYDRATION MESSAGE BANK
// Canonical catalog lives in shared/notifBank.ts (from Notif Bank CSV)
// ============================================================================

export {
  HYDRATION_MESSAGES,
  getHydrationReminderMessage,
  type HydrationMessage,
} from './notifBank';

// ============================================================================
// END-OF-DAY BADGE SYSTEM
// Volume and Frequency badges: shared/badgeCalculation.ts
// Time badges remain here for mode display.
// ============================================================================

export {
  FREQUENCY_BADGES,
  VOLUME_BADGES,
  getFrequencyBadge,
  getVolumeBadge,
  calculateBadges,
  calculateCurrentExpectedVolume,
  calculateCurrentVolumePercentage,
  type FrequencyBadge,
  type FrequencyBadgeColor,
  type FrequencyBadgeId,
  type VolumeBadge,
  type BadgeCalculationInput,
  type BadgeCalculationResult,
  type DailyBadgeSnapshot,
} from './badgeCalculation';

// Time-Based Badges (Mode Names - based on active hours)
// Beast Mode removed — 12–16h uses Tank (Requirements: Tank 14–16; Beast dropped).
export const TIME_BADGES = [
  { minHours: 0, maxHours: 6, name: 'Spark', emoji: '⚡', description: 'Quick session' },
  { minHours: 6, maxHours: 8, name: 'Spark', emoji: '⚡', description: '6-8 hours' },
  { minHours: 8, maxHours: 10, name: 'Cruise', emoji: '🚗', description: '8-10 hours' },
  { minHours: 10, maxHours: 12, name: 'Standard', emoji: '📊', description: '10-12 hours' },
  { minHours: 12, maxHours: 16, name: 'Tank', emoji: '🛡️', description: '12-16 hours' },
  { minHours: 16, maxHours: 24, name: 'God', emoji: '👑', description: 'Always on' },
] as const;

export type TimeBadge = (typeof TIME_BADGES)[number];

export function getTimeBadge(activeHours: number): TimeBadge {
  for (let i = TIME_BADGES.length - 1; i >= 0; i--) {
    if (activeHours >= TIME_BADGES[i].minHours) {
      return TIME_BADGES[i];
    }
  }
  return TIME_BADGES[0];
}

// Combined end-of-day badge result
export interface EndOfDayBadges {
  frequency: import('./badgeCalculation').FrequencyBadge;
  volume: import('./badgeCalculation').VolumeBadge;
  time: TimeBadge;
  summary: string;
}
